import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as LocationIcon } from '../../assets/icons/location-icon.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as FacilityIcon } from '../../assets/icons/sidebar/sidebar-facility.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import { FacilityTags, RequestFacilityTagsInterFace } from '../../interface/facility-tag.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import {
  fetchFacilityTags,
  fetchListFilters,
  setFacilityTagsFilterData,
  setSortData,
  updateFacilityFilters,
} from '../../redux/slices/Partners/FacilityTagSlice';
import { RootState } from '../../redux/store';
import MyButton from '../../shared-components/button/Button';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import FacilitySidePanel from '../../shared-components/table-side-panel/partner/FacilitySidePanel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';

import AddFacilityTag from './components/AddFacilityTag';

/**
 * Facility Tag management
 */
function FacilityTagComponent() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('partnerFacilityManagement');
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [isSidePanelOpen, setSidePanelOpen] = useState(false);
  const [currentRowData, setCurrentRowData] = useState<FacilityTags | null>(null);
  const {
    facilityTags,
    page,
    limit,
    totalPages,
    totalResults,
    loading,
    filters,
    filterListLoading,
    sortBy,
    sortField,
  } = useAppSelector((states: RootState) => states.partnersFacilityTags);
  const { currentFacilityTagsList } = useAppSelector(
    (state: RootState) => state.partnersFacilityTags,
  );

  /**
   * Handle the fetch data for Api call function
   * @param {any} data RequestFacilityTagsInterFace
   */
  const fetchData = (data: RequestFacilityTagsInterFace) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchFacilityTags({
          ...data,
        }),
      );
    }
  };

  /**
   * Function to handle data limit change
   * @param {any} value - The value
   */
  const handleLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      ...value,
    });
  };

  // /**
  //  * Handles the pagination
  //  *
  //  */
  // const handlePageChange = (changePage: number) => {
  //   const queryParameters = GetParametersFromCommonFilters(filters);
  //   fetchData({
  //     ...queryParameters,
  //     sortField,
  //     sortBy,
  //     page: changePage,
  //     limit,
  //   });
  // };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      setFacilityTagsFilterData({
        ...sortData,
      }),
    );
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Charging Station
   * @param {any} facilityTag FacilityTags
   * @returns {any} JSX.Element
   */
  const renderChargingStation = (facilityTag: FacilityTags) => (
    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
      {facilityTag?.chargingStations?.map((chargingStation) => (
        <div role="presentation" key={chargingStation._id}>
          <div className="partner-facility-tag-management-wrap__tag-label">
            <span style={{ fontWeight: '700' }}>{chargingStation.name}</span>
          </div>
        </div>
      ))}
    </div>
  );

  const column = [
    {
      label: t('facilityName'),
      accessorKey: 'facilityTagName' as keyof FacilityTags,
      sortAllow: true,
    },

    {
      label: t('location'),

      /**
       * Location view in table
       * @param {any} facilityTag FacilityTags
       * @returns {any} JSX.Element
       */
      customData: (facilityTag: FacilityTags) =>
        facilityTag.address ? (
          <div className="partner-facility-tag-management-wrap__location">
            <span>
              <LocationIcon />
            </span>
            <p className="partner-facility-tag-management-wrap__row-data">{facilityTag.address}</p>
          </div>
        ) : (
          <div>{t('na')}</div>
        ),
      accessorKey: '' as keyof FacilityTags,
      columnWidth: 150,
    },
    {
      label: t('chargingStations'),
      accessorKey: 'chargingStationsData' as keyof FacilityTags,
      customData: renderChargingStation,
    },
  ];

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, limit, page, sortBy, sortField });
  };

  /**
   * Handle the open side panel on row click function
   * @param {any} _ string
   * @param {any} row FacilityTags
   */
  const handleOpenSidePanelOnRowClick = (_: string, row: FacilityTags) => {
    if (!row?._id) return;

    const parameters = new URLSearchParams(searchParameters);

    parameters.set('facilityId', row?._id);
    setSearchParameters(parameters);

    setSidePanelOpen(false); // close add panel
    setCurrentRowData(row);
  };

  /**
   * Add account function
   */
  const addAccount = () => {
    const parameters = new URLSearchParams(searchParameters);
    parameters.delete('facilityId'); // close row panel if open
    setSearchParameters(parameters);

    setSidePanelOpen(true);
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      limit: DefaultApiLimit.limit,
      page: DefaultApiLimit.page,
    });
  }, [currentFacilityTagsList, filters]);

  useEffect(() => {
    if (searchParameters.get('facilityId')) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('facilityId');
      setSearchParameters(parameters, { replace: true });
    }
  }, []);

  useEffect(() => {
    const hasSidePanelParameter = searchParameters.get('facilityId') || searchParameters.get('add');

    if (!hasSidePanelParameter) {
      dispatch(
        setPageTitle({
          title: t('facilities'),
          icon: <FacilityIcon />,
          count: totalResults,
        }),
      );
    } else {
      dispatch(
        setPageTitle({
          title: t('facilities'),
          icon: <FacilityIcon />,
          count: undefined,
        }),
      );
    }
  }, [searchParameters, totalResults, t]);

  return (
    <div className="partner-facility-tag-management-wrap">
      <div className="partner-facility-tag-management-wrap__table-container">
        <CustomTable
          dataKey="Facility"
          loading={loading}
          limit={limit}
          header={column}
          data={facilityTags}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          // handlePageChange={handlePageChange}
          pageLimitChange={handleLimitChange}
          filters={filters}
          filtersToDispatch={updateFacilityFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          setHeightAutoToTr
          refreshRequired
          refershCurrentPages={handleRefresh}
          hoverLabel={t('refresh')}
          serialNo
          rowClickEvent={handleOpenSidePanelOnRowClick}
          rowClickParamsKey="facilityId"
          rowClickSidePanel={
            <FacilitySidePanel fetchIdFrom="queryParams" currentData={currentRowData} />
          }
          sidePanelEnabled={isSidePanelOpen}
          sidePanelManualContent={
            <AddFacilityTag
              handleCancel={() => {
                setSidePanelOpen(false);
              }}
            />
          }
          onSidePanelClose={() => {
            const parameters = new URLSearchParams(searchParameters);
            parameters.delete('facilityId');
            setSearchParameters(parameters);
            setSidePanelOpen(false);
          }}
          rightTopContent={
            <div className="partner-facility-tag-management-wrap__button-list">
              <div className="partner-facility-tag-management-wrap__button-item">
                <MyButton
                  label={t('addFacility')}
                  buttonType="submit"
                  onClickFunc={addAccount}
                  svgIcon={<PlusIcon />}
                />
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
}

export default FacilityTagComponent;
